import { useState, useEffect } from 'react'

export interface IPLocation {
  country: string
  countryCode: string
  region: string
  regionName: string
  city: string
  zip: string
  lat: number
  lon: number
  timezone: string
}

interface IPWhoIsResponse {
  success?: boolean
  message?: string
  city?: string
  region?: string
  country?: string
  country_code?: string
  latitude?: number
  longitude?: number
  postal?: string
  timezone?: string | { id?: string }
}

export function useIPLocation() {
  const [location, setLocation] = useState<IPLocation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()

    const fetchLocation = async () => {
      try {
        setLoading(true)
        const response = await fetch('https://ipwho.is/', {
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error('Failed to fetch location')
        }

        const data = (await response.json()) as IPWhoIsResponse

        if (!data.success) {
          throw new Error(data.message || 'Failed to fetch location')
        }

        const timezone = typeof data.timezone === 'string' ? data.timezone : (data.timezone?.id ?? '')

        setLocation({
          city: data.city || '',
          region: data.region || '',
          country: data.country || '',
          countryCode: data.country_code || '',
          lat: data.latitude || 0,
          lon: data.longitude || 0,
          timezone,
          regionName: data.region || '',
          zip: data.postal || '',
        })
        setError(null)
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return
        setError(err instanceof Error ? err.message : 'An error occurred')
        setLocation(null)
      } finally {
        setLoading(false)
      }
    }

    fetchLocation()

    return () => {
      controller.abort()
    }
  }, [])

  return { location, loading, error }
}
